import { useMemo } from "react";
import { Typography, Box, SvgIcon, Tooltip, Stack } from "@mui/material";
import { GppMaybeOutlined } from "@mui/icons-material";
import FileTypeIcon from "components/base/FileTypeIcon";
import { devViewPdf } from "config";
import NewsguardScore from "components/base/NewsguardScore";
import AllSidesRating from "components/base/all-sides/AllSidesRating";
import CharityNavigatorScore from "components/base/CharityNavigatorScore";
import SearchResultLink from "features/SourceSearch/components/SearchResultLink";
import { getRegularDate, getFormattedDate } from "utils/time.util";
import { useDispatch } from "react-redux";
import {
  setOthersNeeded,
  setIsSourceSidebarOpen,
  setSourceUrl,
  setEntityName,
} from "context/features/dataSlice";

import { ReactComponent as summarizerLogo } from "assets/icons/summarizer-icon.svg";
import { ReactComponent as citationLogo } from "assets/icons/citation-generator-icon.svg";
import { ReactComponent as readingLogo } from "assets/icons/reading-level-icon.svg";
import { useLocation } from "react-router-dom";


export const useColumns = ({
  fields = [],
  persistedWidths = {},
  commonWidth,
  theme,
  handlers = {},
  defaultFlexes = {},
  headerNames = {}
}) => {
  const { pathname } = useLocation();
  const isPublicCollection = pathname.includes("/public");
  const { handleSidebarOpen, setCurrentPublicCollection, navigate } = handlers;
  const dispatch = useDispatch();

  const handleSourceSidebarOpen = (row) => {
    dispatch(setOthersNeeded(true));
    dispatch(setIsSourceSidebarOpen(true));
    dispatch(setSourceUrl(row.hostName || row.sourceUrl_chr));
    dispatch(setEntityName(row.sourceName_chr));
  };

  const defaultFlex = {
    title_chr: 2,
    ...defaultFlexes
  };

  const withWidth = (field) =>
    persistedWidths[field]
      ? { flex: 0, width: persistedWidths[field], minWidth: 100 }
      : commonWidth !== null
        ? { flex: 0, width: commonWidth, minWidth: 100 }
        : { flex: defaultFlex[field] || 1, minWidth: 100 };

  const renderers = {
    createdDateTime_dtm: {
      type: "date",
      field: "createdDateTime_dtm",
      headerName: "Added",
      renderCell: (params) => (
        <Typography
          noWrap
          variant="inherit"
          color={theme.palette.text.secondary}
          onClick={() => handleSidebarOpen?.(params.row, "notes")}
          sx={{ cursor: "pointer" }}
        >
          {getRegularDate(params.value) || "-"}
        </Typography>
      ),
      valueGetter: (value) => (value ? new Date(value) : null),
    },
    lastUpdatedDateTime_dtm: {
      headerName: "Updated",
      type: "date",
      renderCell: (params) => (
        <Typography noWrap variant="inherit">
          {getFormattedDate(params.value)}
        </Typography>
      ),
      valueGetter: (value) => (value ? new Date(value) : null),
    },
    title_chr: {
      field: "title_chr",
      headerName: "Title",
      renderCell: (params) => {
        const { sourceUrl_chr, hostName, mediaBiasRating, score } = params.row;
        return sourceUrl_chr ? (
          <Stack direction="row" alignItems="center" gap={0.5}>
            {params.row.isMisinformationSource_ysn && (
              <Tooltip arrow title="Warning: This may contain misinformation">
                <GppMaybeOutlined color="warning" />
              </Tooltip>
            )}
            <SearchResultLink
              url={sourceUrl_chr}
              trackingCat="click_resultset_title"
              trackingOptions={{
                url: sourceUrl_chr,
                entity: hostName,
                mediabiasrating: mediaBiasRating ?? "NA",
                score: score ?? "NA",
                type: "bookmark",
                title: params.value || "-",
                date_published: params.row.sourcePublishedDateTime_dtm,
              }}
              variant="inherit"
              noWrap
              sx={{
                fontSize: "1.3rem",
                cursor: "pointer",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {params.value || "-"}
            </SearchResultLink>
            {devViewPdf && <FileTypeIcon url={sourceUrl_chr} />}
          </Stack>
        ) : (
          <Typography variant="inherit" noWrap sx={{ fontSize: "1.3rem" }}>
            {params.value || "-"}
          </Typography>
        );
      },
    },
    collectionName_chr: {
      field: "collectionName_chr",
      headerName: headerNames["collectionName_chr"] ? headerNames["collectionName_chr"] : "Collection",
      renderCell: (params) => (
        <Typography
          variant="inherit"
          noWrap
          fontSize="1.3rem"
          fontWeight={!isPublicCollection ? "bold" : "normal"}
          color={isPublicCollection ? "": "#6c6c6c"}
          onClick={() => handleSidebarOpen?.(params.row, "notes")}
          sx={{ cursor: "pointer" }}
        >
          {params.value || "-"}
        </Typography>
      ),
    },
    sourceName_chr: {
      field: "sourceName_chr",
      headerName: "Source",
      renderCell: (params) => (
        <Typography
          noWrap
          variant="inherit"
          fontSize="1.3rem"
          fontWeight="bold"
          color="#6c6c6c"
          onClick={() => handleSourceSidebarOpen(params.row)}
          sx={{ cursor: "pointer" }}
        >
          {params.value || "-"}
        </Typography>
      ),
    },
    sourcePublishedDateTime_dtm: {
      field: "sourcePublishedDateTime_dtm",
      headerName: "Pub Date",
      renderCell: (params) => (
        <Typography
          variant="inherit"
          noWrap
          color="#6c6c6c"
          onClick={() => handleSidebarOpen?.(params.row, "notes")}
          sx={{ cursor: "pointer" }}
        >
          {getRegularDate(params.value) || "-"}
        </Typography>
      ),
      valueGetter: (value) => (value ? new Date(value) : null),
    },
    score: {
      field: "score",
      headerName: "NewsGuard",
      renderCell: (params) =>
        params.value ? (
          <Box
            onClick={() => handleSourceSidebarOpen(params.row)}
            sx={{ display: "flex", alignItems: "center", minHeight: "100%", cursor: "pointer" }}
          >
            {params.value ? (
              <NewsguardScore score={Number(params.value)} size="small" />
            ) : (
              "-"
            )}
          </Box>
        ) : (
          "-"
        ),
    },
    mediaBiasRating: {
      field: "mediaBiasRating",
      headerName: "AllSides",
      renderCell: (params) =>
        params.value ? (
          <Box
            onClick={() => handleSourceSidebarOpen(params.row)}
            sx={{ display: "flex", alignItems: "center", minHeight: "100%", cursor: "pointer" }}
          >
            {params.value ? (
              <AllSidesRating activeCategory={params.value} single={true} />
            ) : (
              "-"
            )}
          </Box>
        ) : (
          "-"
        ),
    },
    charityNavigatorSource: {
      field: "charityNavigatorSource",
      headerName: "Charity Navigator Rating",
      renderCell: (params) =>
        params.row.charityNavigatorScore ? (
          <Box
            onClick={() => handleSourceSidebarOpen(params.row)}
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              cursor: "pointer",
              width: "100%",
            }}
          >
            <CharityNavigatorScore
              score={params.row.charityNavigatorScore}
              size="small"
            />
          </Box>
        ) : (
          "-"
        ),
    },
    isSummaryAvailable_ysn: {
      field: "isSummaryAvailable_ysn",
      headerName: "Summary",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            width: "100%",
            height: "100%",
            "&:hover > svg": {
              fill: params.value
                ? theme.palette.primary.dark
                : theme.palette.mode === "dark"
                  ? theme.palette.grey[300]
                  : theme.palette.grey[700],
            },
          }}
          onClick={() => {
            handleSidebarOpen(params.row, "summary");
          }}
        >
          <SvgIcon
            component={summarizerLogo}
            inheritViewBox
            sx={{
              fill: params.value
                ? theme.palette.primary.main
                : theme.palette.mode === "dark"
                  ? theme.palette.grey[800]
                  : theme.palette.grey[200],
              height: "1.8rem",
            }}
          />
        </Box>
      ),
    },
    isCitationAvailable_ysn: {
      field: "isCitationAvailable_ysn",
      headerName: "Citation",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            width: "100%",
            height: "100%",
            "&:hover > svg": {
              fill: params.value
                ? theme.palette.primary.dark
                : theme.palette.mode === "dark"
                  ? theme.palette.grey[300]
                  : theme.palette.grey[700],
            },
          }}
          onClick={() => {
            handleSidebarOpen(params.row, "citation");
          }}
        >
          <SvgIcon
            component={citationLogo}
            inheritViewBox
            sx={{
              fill: params.value
                ? theme.palette.primary.main
                : theme.palette.mode === "dark"
                  ? theme.palette.grey[800]
                  : theme.palette.grey[200],
              height: "1.8rem",
            }}
          />
        </Box>
      ),
    },
    isReadingLevelAvailable_ysn: {
      field: "isReadingLevelAvailable_ysn",
      headerName: "Leveling",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            width: "100%",
            height: "100%",
            "&:hover > svg": {
              fill: params.value
                ? theme.palette.primary.dark
                : theme.palette.mode === "dark"
                  ? theme.palette.grey[300]
                  : theme.palette.grey[700],
            },
          }}
          onClick={() => {
            handleSidebarOpen(params.row, "readinglevel");
          }}
        >
          <SvgIcon
            component={readingLogo}
            inheritViewBox
            sx={{
              fill: params.value
                ? theme.palette.primary.main
                : theme.palette.mode === "dark"
                  ? theme.palette.grey[800]
                  : theme.palette.grey[200],
              height: "1.8rem",
            }}
          />
        </Box>
      ),
    },
    notesCount_cnt: {
      field: "notesCount_cnt",
      headerName: "Notes",
      renderCell: (params) => (
        <Typography
          onClick={() => handleSidebarOpen(params.row, "notes")}
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            cursor: "pointer",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    collectionMemberships: {
      field: "collectionMemberships",
      headerName: "Collections",
      sortable: false,
      renderCell: (params) => {
        const memberships = params.value || [];
        if (memberships.length === 0) {
          return (
            <Typography variant="inherit" noWrap>
              -
            </Typography>
          );
        }
        const extraCount = memberships.length - 1;
        const primaryName = memberships[0].collectionName_chr;
        if (extraCount === 0) {
          return (
            <Tooltip arrow title={primaryName || ""}>
              <Typography variant="inherit" noWrap>
                {primaryName}
              </Typography>
            </Tooltip>
          );
        }
        const names = memberships
          .map((membership) => membership.collectionName_chr)
          .join(", ");
        return (
          <Tooltip arrow title={names}>
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0, width: "100%" }}>
              <Typography variant="inherit" noWrap sx={{ flex: "0 1 auto", minWidth: 0 }}>
                {primaryName}
              </Typography>
              <Typography variant="inherit" noWrap sx={{ flex: "0 0 auto" }}>
                {`+${extraCount}`}
              </Typography>
            </Stack>
          </Tooltip>
        );
      },
    },
    userName_chr: {
      field: "userName_chr",
      headerName: "Owner",
      renderCell: (params) => (
        <Typography variant="inherit" noWrap fontSize="1.4rem">
          @{params.value}
        </Typography>
      ),
    },
    publicCollectionSource_Count_int: {
      field: "publicCollectionSource_Count_int",
      headerName: "Source Count",
      renderCell: (params) => (
        <Typography variant="inherit" noWrap fontSize="1.4rem">
          {params.value}
        </Typography>
      ),
    },
  };

  return useMemo(() => {
    return fields
      .map((field) => ({
        field,
        ...withWidth(field),
        ...renderers[field],
      }))
      .filter(Boolean);
  }, [fields, persistedWidths, commonWidth, theme]);
};
